import ActivityPoints from "@components/ActivityPoints";
import BrandLogo from "@components/BrandLogo";
import Button from "@components/Button";
import { Navigate, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import StickyButton from "../../components/StickyButton";
import "./style.scss";
import { sendEvent } from "../../Helpers";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import {
  getGameRewardPrizeRedeemPath,
  getModuleValue,
  parseCMSBold,
  parseHTMLToText,
  useWindowSize,
} from "../../Helpers";
import {
  ACTIVATION_CACHE_TIME,
  getActivationDescribe,
} from "../../api/activation";
import { getPrizeList } from "../../api/prize";
import { getCouponList } from "../../api/coupon";
import { getPackScanCount, packStats } from "../../api/pack";
import StickyButtonNav from "@components/StickyButtonNav";
import DOMPurify from "dompurify";

function ContestResults({ results, draw, prizes }) {
  if (!results) return null;

  if (results.results && results.prizeIdent) {
    if (!draw) return null;

    if (draw.state === "win") {
      return (
        <div className="contest-result-content">
          {results.won && (
            <p
              className="desc"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(results.won),
              }}
            />
          )}
          {!draw.claimed && prizes?.length > 0 && <PrizeList prizes={prizes} />}
          {!draw.claimed && draw.rewardUrl && (
            <Button href={draw.rewardUrl}>
              {results.claimLabel || "Jetzt sichern"}
            </Button>
          )}
        </div>
      );
    }

    if (draw?.state === "lose") {
      return results.lost ? (
        <p
          className="desc"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(results.lost),
          }}
        />
      ) : null;
    }
  }

  return (
    <div>
      {results.text && (
        <div
          className="desc"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(results.text),
          }}
        />
      )}
      {results.img && (
        <img className="contest-result-img" src={results.img} alt="" />
      )}
      {results.winners?.map((winner, index) => (
        <div className="contest-result-winner" key={index}>
          {winner.copy && (
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(winner.copy),
              }}
            />
          )}
          {winner.name && <p className="name">{winner.name}</p>}
        </div>
      ))}
    </div>
  );
}

function PrizeList({ prizes }) {
  return (
    <div className="contest-rewards">
      <ul className="contest-rewards-list">
        {prizes.map((item, index) => (
          <li key={index} className="contest-rewards-item">
            <div
              className="contest-rewards-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.name),
              }}
            />
            <div className="contest-rewards-image">
              <img src={item.img} alt="" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function IntroContestTemplate() {
  const { ident, page } = useParams();
  const canReplayWhenCompleted = [
    "tml2026_dj_console_vuse",
    "tml2026_dj_console_glo",
  ].includes(ident);
  const { width } = useWindowSize();
  const XGA = width >= 1020;
  const navigate = useNavigate();

  const { data, isError, isLoading } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      getActivationDescribe({
        activation: {
          ident: ident,
          withSolutions: true,
        },
      }),
    retry: false,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const activationQuestionsQuery = useQuery({
    queryKey: ["game-questions", ident],
    queryFn: async () => {
      let data;
      try {
        data = await import(`./Activities/Idents/${ident}/data.js`);
        return data.gameData;
      } catch (e) {
        console.error(e);
        throw new Error("Activation doesn't exist");
      }
    },
    staleTime: 1000 * 60 * 60,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const gameData = activationQuestionsQuery.data;
  const requiredPacks = Number(gameData?.requiredPacks) || 0;
  const { data: packStatsData, isFetched: packStatsFetched } = useQuery({
    queryKey: ["pack-stats"],
    queryFn: async () => (await packStats()) ?? null,
    enabled: requiredPacks > 0,
    suspense: false,
    retry: false,
    refetchOnMount: true,
  });

  const results = gameData?.results;
  const prizeIdent = results?.prizeIdent;
  const usesApiResults = results?.results === true && Boolean(prizeIdent);
  const {
    data: prizeListData,
    isFetched: prizeListFetched,
    isError: prizeListError,
  } = useQuery({
    queryKey: ["contest-prize-list", prizeIdent],
    queryFn: async () => (await getPrizeList()) ?? { prizes: [] },
    enabled: usesApiResults && page === "results",
    suspense: false,
    retry: false,
    refetchOnMount: "always",
  });

  const {
    data: couponListData,
    isFetched: couponListFetched,
    isError: couponListError,
  } = useQuery({
    queryKey: ["contest-coupon-list", prizeIdent],
    queryFn: async () => (await getCouponList()) ?? { coupon: [] },
    enabled: usesApiResults && page === "results",
    suspense: false,
    retry: false,
    refetchOnMount: "always",
  });

  if (activationQuestionsQuery.isLoading || isLoading) {
    return null;
  }

  if (
    (activationQuestionsQuery.isError && !activationQuestionsQuery.isLoading) ||
    (isError && !isLoading) ||
    !data
  ) {
    return <Navigate to="/activities" replace />;
  }

  if (ident.includes("poll_1")) {
    return <Navigate to={`/activities/${ident}/gra`} replace />;
  }

  const showContestTabs = gameData?.contestTabs !== false;
  const stayOnIntro = data?.type === "Contest" || Boolean(gameData?.contest);
  const scans = Math.min(getPackScanCount(packStatsData), requiredPacks || 0);
  const entryUnlocked =
    requiredPacks === 0 || (packStatsFetched && scans >= requiredPacks);
  const joined = data.completed && !canReplayWhenCompleted;
  const entryBlocked =
    !entryUnlocked || data.expired || (joined && stayOnIntro);

  const customBtnTextStart = getModuleValue(
    data?.content?.modules,
    "activity_button",
    "metadata.titleStart",
  );
  const customBtnTextEnd = getModuleValue(
    data?.content?.modules,
    "activity_button",
    "metadata.titleCompleted",
  );
  const customBtnUrlStart = getModuleValue(
    data?.content?.modules,
    "activity_button",
    "metadata.urlStart",
  );

  const wonPrize = usesApiResults
    ? (prizeListData?.prizes?.find(
        (prize) => prize?.catalogue?.ident === prizeIdent,
      ) ?? null)
    : null;
  const claimedCoupon = usesApiResults
    ? (couponListData?.coupon?.find(
        (coupon) => coupon?.catalogue?.ident === prizeIdent,
      ) ?? null)
    : null;
  const contestDraw = !usesApiResults
    ? null
    : !prizeListFetched || prizeListError
      ? null
      : wonPrize
        ? {
            state: "win",
            rewardUrl: getGameRewardPrizeRedeemPath(wonPrize),
          }
        : !couponListFetched || couponListError
          ? null
          : claimedCoupon
            ? { state: "win", claimed: true }
            : { state: "lose" };

  const getUserSystem = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/windows phone/i.test(userAgent)) return "Windows Phone";
    if (/win/i.test(userAgent)) return "Windows";
    if (/android/i.test(userAgent)) return "Android";
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
    if (/mac/i.test(userAgent)) return "macOS";
    if (/linux/i.test(userAgent)) return "Linux";
    return "Unknown";
  };

  const buttonLabel = stayOnIntro
    ? joined
      ? customBtnTextEnd || "Bereits teilgenommen"
      : data.expired
        ? "Aktivität abgeschlossen"
        : customBtnTextStart || "Jetzt teilnehmen"
    : (!data.completed || canReplayWhenCompleted) && customBtnTextStart
      ? customBtnTextStart
      : (data.completed && !canReplayWhenCompleted) || data.expired
        ? "Aktivität abgeschlossen"
        : "Los geht's";

  const handleStart = () => {
    if (entryBlocked && stayOnIntro) return;

    const system = getUserSystem();
    sendEvent(`${ident}_START`);
    sendEvent(`${ident}_SYS_${system}`);

    let targetUrl = "";
    if (!data.completed && customBtnUrlStart) {
      targetUrl = customBtnUrlStart;
    } else if ((data.completed && !canReplayWhenCompleted) || data.expired) {
      targetUrl = "/activities";
    } else {
      targetUrl = `/activities/${ident}/gra`;
    }

    setTimeout(() => {
      navigate(targetUrl);
    }, 200);
  };

  return (
    <section className="activities-intro-page contest-intro-page">
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                parseCMSBold(data?.content?.description),
              ),
            }}
          ></p>
        </PagesHeader>
        <div className="desktop-wrapper">
          <div className="activity-img">
            <img
              src={getModuleValue(
                data.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
              className="mobile-img"
            />
            <img
              src={getModuleValue(
                data.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
              className="desktop-img"
            />
            {!!data.payout && (
              <ActivityPoints desktopBig={ident.includes("konkurs_")}>
                {data.payout}
              </ActivityPoints>
            )}

            <BrandLogo brands={data.brands} />
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    action={handleStart}
                    disabled={stayOnIntro && entryBlocked}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
          <div className="main-content">
            {showContestTabs && (
              <StickyButtonNav
                items={[
                  { label: "Details", link: `/contest/${ident}` },
                  { label: "Prämien", link: `/contest/${ident}/rewards` },
                  { label: "Ergebnisse", link: `/contest/${ident}/results` },
                ]}
              />
            )}
            {!page && (
              <div>
                <p
                  className="desc"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      parseHTMLToText(
                        getModuleValue(
                          data.content?.modules,
                          "intro",
                          "custom",
                        ),
                        "p",
                      ),
                    ),
                  }}
                ></p>
                {requiredPacks > 0 && (
                  <div className="scan-progress">
                    <div className="bars" aria-hidden="true">
                      {Array.from({ length: requiredPacks }).map((_, index) => (
                        <span
                          key={index}
                          className={index < scans ? "filled" : ""}
                        />
                      ))}
                    </div>
                    <p className="label">
                      ABGESCHLOSSEN:
                      <br />
                      {scans}/{requiredPacks} SCANS
                    </p>
                  </div>
                )}
                <div className="how-to-box">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        getModuleValue(
                          data.content?.modules,
                          "how_to_use_activity",
                          "custom",
                        ),
                      ),
                    }}
                  ></p>
                </div>
                {getModuleValue(
                  data.content?.modules,
                  "terms_pdf",
                  "file.url",
                ) && (
                  <a
                    href={getModuleValue(
                      data.content?.modules,
                      "terms_pdf",
                      "file.url",
                    )}
                    className="terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getModuleValue(
                      data.content?.modules,
                      "terms_pdf",
                      "metadata.desc",
                    )}
                  </a>
                )}
              </div>
            )}
            {showContestTabs &&
              page === "rewards" &&
              gameData?.prizes?.length > 0 && (
                <PrizeList prizes={gameData.prizes} />
              )}
            {showContestTabs && page === "results" && (
              <ContestResults
                results={results}
                draw={contestDraw}
                prizes={gameData?.prizes}
              />
            )}
          </div>
        </div>
        {!XGA && (
          <StickyButton
            action={handleStart}
            disabled={stayOnIntro && entryBlocked}
          >
            {buttonLabel}
          </StickyButton>
        )}
      </div>
    </section>
  );
}
